import React from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { PRACTICE_INFO } from "@/site-data";
import { ArrowRight, Download, FileText, LockKeyhole, Phone, ShieldCheck } from "lucide-react";

const formItems = [
  {
    title: "New Patient Forms",
    description: "Complete the confidential medical, insurance, medication, and health-history information requested before your first visit.",
    note: "Official office PDF · 6 pages",
    href: "/manus-storage/new-patient-form_ac3f6ba9.pdf",
  },
  {
    title: "Consent to Treatment & Financial Agreement",
    description: "Review consent, release-of-information, insurance payment, cancellation, no-show, and financial agreement policies.",
    note: "Official office PDF · 1 page",
    href: "/manus-storage/financial-agreement_cc9178ff.pdf",
  },
  {
    title: "Notice of Privacy Practices",
    description: "Review the office notice about protected health information, permitted uses, disclosures, and your HIPAA rights.",
    note: "Official office PDF · 1 page",
    href: "/manus-storage/privacy-practices-form_3acbba0a.pdf",
  },
  {
    title: "Financial Hardship Form",
    description: "Request a financial hardship review. The office recommends submitting sensitive supporting documentation in person.",
    note: "Official office PDF · 2 pages",
    href: "/manus-storage/financial-hardship-form_5bbc927a.pdf",
  },
];

export default function Forms() {
  const formsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Patient Forms | Wasatch Foot & Ankle Center",
    url: "https://www.drspenceharper.com/forms/",
    description: "New patient, consent, privacy, and financial hardship forms for Wasatch Foot & Ankle Center in Heber City, Utah.",
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Patient Forms | Wasatch Foot & Ankle Center Heber City"
        description="Access official new patient, consent, privacy, and financial hardship forms for Wasatch Foot & Ankle Center in Heber City, Utah."
        canonicalPath="/forms/"
        schema={formsSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "Patient Forms" }]} />

      <main className="flex-1">
        <section className="bg-[#0f2b48] text-white py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 border border-cyan-300/30 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-cyan-200">
              <FileText className="w-4 h-4" /> Prepare for your visit
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Patient Forms</h1>
            <p className="mx-auto max-w-2xl text-base sm:text-lg leading-relaxed text-slate-200">
              Review and download the official forms provided by Wasatch Foot &amp; Ankle Center before your visit.
            </p>
          </div>
        </section>

        <section className="bg-slate-50 py-14 sm:py-18">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {formItems.map((item) => (
                <article key={item.title} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-50 text-[#0284c7]">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="space-y-2">
                      <h2 className="font-heading text-xl font-bold text-[#0f2b48]">{item.title}</h2>
                      <p className="text-sm leading-relaxed text-slate-600">{item.description}</p>
                      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{item.note}</p>
                    </div>
                  </div>
                  <div className="mt-6 border-t border-slate-100 pt-5">
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-bold text-[#0284c7] hover:text-[#0369a1]"
                    >
                      <Download className="w-4 h-4" /> Download PDF <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </article>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-3xl bg-[#e8f5f5] border border-[#c8e3e4] p-7">
                <div className="flex items-start gap-3">
                  <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-[#0284c7]" />
                  <div className="space-y-2">
                    <h2 className="font-heading text-xl font-bold text-[#0f2b48]">Protect your health information</h2>
                    <p className="text-sm leading-relaxed text-slate-700">
                      These documents contain sensitive health, insurance, and financial information. Do not submit completed forms through the website request form or ordinary email. Call the office for secure intake instructions.
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                  <div className="space-y-2">
                    <h2 className="font-heading text-xl font-bold text-[#0f2b48]">Need help before your visit?</h2>
                    <p className="text-sm leading-relaxed text-slate-600">
                      Our Heber City team can answer questions about forms, insurance, and what to bring.
                    </p>
                    <a href={`tel:${PRACTICE_INFO.officePhone}`} className="inline-flex items-center gap-2 text-sm font-bold text-[#0284c7] hover:text-[#0369a1]">
                      <Phone className="w-4 h-4" /> {PRACTICE_INFO.officePhone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link href="/appointments/" className="inline-flex items-center gap-2 rounded-xl bg-[#0284c7] px-6 py-3 text-sm font-bold text-white shadow hover:bg-[#0369a1]">
                Request an appointment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
